import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Dialog from 'controls/dialog';
import ActionIndicator from 'controls/action_indicator';
import { DisplayDate } from 'controls/display_date';
import { VerifyStep } from './verify_step/verify_step';
import { sequenceStateUpdater } from './verify_step/sequence_state_updater';
import objUtils from 'utils/object';
import { isExpired } from 'utils';
import { SequenceManager } from 'utils/sequence_manager';

class VerifyBadgeDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      steps: {},
      verified: false,
      expired: false,
      complete: true,
      fauxBadgeSuccess: {
        pending: true,
        succeeded: false
      }
    };
    this.initSequenceManager();
  }

  /**
   * Initializes the sequence manager
   * Note: This modifies the initial state, and should
   * not be called outside of the constructor
   */
  initSequenceManager() {
    this.sequenceManager = new SequenceManager(500, {
      rand: 200,
      onStepComplete: sequenceStateUpdater(this)
    });

    let fauxBadgeSucceeded = () => this.state.fauxBadgeSuccess.succeeded;

    this.sequenceManager
      .addStep("issued on", fauxBadgeSucceeded)
      .addStep("issued by", fauxBadgeSucceeded)
      .addStep("issued using", fauxBadgeSucceeded)
      .addStep("issued to", fauxBadgeSucceeded)
      .addStep("expired or updated", fauxBadgeSucceeded);

    // initializing state steps to false
    for (let step of this.sequenceManager.steps) {
      this.state.steps[step.label] = true;
    }
    setTimeout(() => {
      this.setState({ ...this.state,
        fauxBadgeSuccess: {
          pending: false,
          succeeded: true
        },
        complete: true,
        verified: !this.isExpired(),
        expired: this.isExpired()
      });
    }, 1000);
  }

  stepStatus(label) {
    return {
      succeeded: this.state.fauxBadgeSuccess.succeeded,
      pending: !this.state.steps[label]
    };
  }

  /**
   * Returns true if the verification is complete, but the badge could not be verified, nor is it
   * expired.
   *
   * @returns {Boolean|boolean}
   */
  isUnverifiable() {
    return this.props.complete && !this.props.expired && !this.props.verified;
  }

  /**
   * The component mounted: start loading data.
   */
  componentDidMount() {
    this.sequenceManager.reset();
  }

  /**
   * Clean up anything before unmounting.
   */
  componentWillUnmount() {
    this.sequenceManager.halt();
  }

  /**
   * Show the earner of the badge
   * @returns {String} earner name
   */
  issuedTo() {
    return objUtils.dig(this.props, 'badge.issued_to') || '';
  }

  /**
   * Render a DisplayDate if the date is truthy
   * @returns {DisplayDate}
   */
  conditionalDate(date) {
    if (date) {
      return <DisplayDate date={date} />;
    }
    return '';
  }

  issuedOn() {
    let dateToDisplay = objUtils.dig(this.props, 'badge.issued_at');
    return this.conditionalDate(dateToDisplay);
  }

  /**
   * Show the entity that issued the badge provided in props
   * @returns {String|null} issuer name
   */
  issuedBy() {
    const issuer = objUtils.dig(this.props, 'badge.issuer.display_name');
    if (issuer) {
      return issuer;
    }
    return '';
  }

  issuedUsing() {
    return 'Credly';
  }


  expiredOn() {
    return this.conditionalDate(objUtils.dig(this.props, 'badge.expires_at'));
  }

  /**
   * Check for badge expiration
   * @returns {Boolean}
   */
  isExpired() {
    return isExpired(objUtils.dig(this.props, 'badge.expires_at'));
  }

  /**
   * Check all the statuses, and their success
   * @returns {Boolean}
   */
  isVerified() {
    return (
      this.isComplete &&
      !this.isExpired()
    );
  }

  isComplete() {
    return this.state.fauxBadgeSuccess.succeeded;
  }

  /**
   * Renders the component.
   *
   * @returns {React.element}
   */
  render() {
    return (
      <Dialog
        title={this.props.title}
        show
        shouldClose={this.props.hideDialog}
      >
        <Dialog.Content>
          <Fragment>
            <div className="cr-verify-badge-dialog__steps">
              <VerifyStep
                title="Issued on"
                data={this.issuedOn()}
                status={this.stepStatus('issued on')}
              />
              <VerifyStep
                title="Issued by"
                data={this.issuedBy()}
                status={this.stepStatus('issued by')}
              />
              <VerifyStep
                title="Issued using"
                data={this.issuedUsing()}
                status={this.stepStatus('issued using')}
              />
              <VerifyStep
                title="Issued to"
                data={this.issuedTo()}
                status={this.stepStatus('issued to')}
              />
              {
                this.isExpired()
                  ? <VerifyStep
                    title="Expired on"
                    data={this.expiredOn()}
                    status={this.stepStatus('expired or updated')}
                    failed
                    succeeded={false}
                  />
                  : ""
              }
            </div>
            <div className="cr-verify-badge-dialog__verified">
              <div className="cr-verify-badge-dialog__verified-icon">
                <ActionIndicator
                  persist
                  strictShow
                  pending={!this.state.complete}
                  succeeded={this.state.verified}
                  failed={this.state.expired || this.isUnverifiable()}
                />
              </div>
              { this.state.verified && <div>Verified</div> }
              { this.state.expired && <div>Expired</div> }
              { this.isUnverifiable() && <div>Unverifiable</div> }
            </div>
          </Fragment>
        </Dialog.Content>
      </Dialog>
    );
  }
}

VerifyBadgeDialog.propTypes = {
  complete: PropTypes.bool,
  expired: PropTypes.bool,
  hideDialog: PropTypes.func.isRequired,
  preparing: PropTypes.bool,
  title: PropTypes.string,
  verified: PropTypes.bool,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    action: PropTypes.func.isRequired
  })
};

VerifyBadgeDialog.defaultProps = {
  title: 'Verification'
};

const makeMapStateToProps = () => {
  return (state, props) => {
    return {
      badge: props.badge,
      fetchBadgeStatus: true
    };
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return bindActionCreators({}, dispatch);
};

const ReduxVerifyBadgeObi = connect(makeMapStateToProps, mapDispatchToProps)(VerifyBadgeDialog);

export { ReduxVerifyBadgeObi as VerifyBadgeDialog };